import { Box, Container, Typography, Divider, Paper, List, ListItem, ListItemText } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';

const TermsOfServicePage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ py: { xs: 10, md: 12 } }}>
      <Helmet>
        <title>Terms of Service | SOLVENDA ISP Management Solution</title>
        <meta name="description" content="Read the Terms of Service for SOLVENDA's ISP management platform. These terms govern the use of our services for Internet Service Providers." />
        <meta name="keywords" content="SOLVENDA terms, ISP management terms, service terms, legal terms, ISP software terms" />
        <link rel="canonical" href="https://solvenda.org/terms-of-service" />
      </Helmet>
      <Container maxWidth="md">
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h1" component="h1" gutterBottom>
            Terms of Service
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: October 15, 2024
          </Typography>

          <Typography variant="body1" paragraph>
            Please read these Terms of Service ("Terms", "Terms of Service") carefully before using the SOLVENDA website
            and services operated by SOLVENDA ("us", "we", "our").
          </Typography>
          
          <Typography variant="body1" paragraph>
            Your access to and use of our services is conditioned on your acceptance of and compliance with these Terms.
            These Terms apply to all visitors, users, and others who access or use our services.
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            By accessing or using our services, you agree to be bound by these Terms. If you disagree with any part of the terms,
            you may not access our services.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            1. Services Description
          </Typography>
          
          <Typography variant="body1" paragraph>
            SOLVENDA provides Internet Service Provider (ISP) management solutions, including but not limited to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Customer management and billing systems" />
            </ListItem>
            <ListItem>
              <ListItemText primary="IPTV service management tools" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Network monitoring and management" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Financial reporting and analytics" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Integration with third-party services" />
            </ListItem>
          </List>
          
          <Typography variant="body1" paragraph>
            The features and functionality of our services may vary based on the subscription plan you select. 
            We reserve the right to modify, suspend, or discontinue any part of our services at any time, with or without notice.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            2. User Accounts
          </Typography>
          
          <Typography variant="body1" paragraph>
            When you create an account with us, you must provide accurate, complete, and current information at all times. 
            Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account.
          </Typography>
          
          <Typography variant="body1" paragraph>
            You are responsible for safeguarding the password that you use to access our services and for any activities 
            or actions under your password. We recommend using strong, unique passwords and enabling two-factor authentication when available.
          </Typography>
          
          <Typography variant="body1" paragraph>
            You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            3. Subscription and Payments
          </Typography>
          
          <Typography variant="body1" paragraph>
            Our services are offered on a subscription basis. You agree to pay the fees for the selected subscription plan in accordance with the pricing and payment terms presented to you at the time of purchase.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Payment terms and conditions:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText 
                primary="Billing Cycle" 
                secondary="Subscriptions are billed in advance on a monthly, quarterly, or annual basis, depending on your chosen billing cycle"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Automatic Renewal" 
                secondary="Subscriptions automatically renew unless cancelled at least 30 days before the end of the current period"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Payment Methods" 
                secondary="We accept payment via credit card, bank transfer, and other payment methods as specified during the checkout process"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Price Changes" 
                secondary="We reserve the right to adjust pricing for our services. Any price changes will be communicated at least 30 days in advance"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Taxes" 
                secondary="Prices listed do not include applicable taxes. Any taxes will be calculated and added at checkout"
              />
            </ListItem>
          </List>
          
          <Typography variant="body1" paragraph>
            Refunds are processed in accordance with our Refund Policy. Generally, no refunds are provided for partial subscription periods or unused services.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            4. Acceptable Use
          </Typography>
          
          <Typography variant="body1" paragraph>
            You agree to use our services only for lawful purposes and in accordance with these Terms. You agree not to use our services:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText primary="In any way that violates any applicable national or international law or regulation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To transmit, or procure the sending of, any advertising or promotional material, including 'spam' or any other similar solicitation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To impersonate or attempt to impersonate SOLVENDA, a SOLVENDA employee, another user, or any other person or entity" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of our services, or which may harm SOLVENDA or users of our services" />
            </ListItem>
          </List>
          
          <Typography variant="body1" paragraph>
            Additionally, you agree not to:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText primary="Use our services in any manner that could disable, overburden, damage, or impair our systems" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Attempt to gain unauthorized access to any parts of our services, computer systems, or user accounts" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Use any robot, spider, or other automatic device to access our services for any purpose" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Introduce any viruses, trojan horses, worms, logic bombs, or other malicious code" />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            5. Intellectual Property
          </Typography>
          
          <Typography variant="body1" paragraph>
            Our services and their original content, features, and functionality are and will remain the exclusive property of SOLVENDA and its licensors. 
            Our services are protected by copyright, trademark, and other intellectual property laws.
          </Typography>
          
          <Typography variant="body1" paragraph>
            You may not:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText primary="Modify, distribute, or create derivative works based on our services or their content" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Use any illustrations, photographs, logos, or other content without our express written consent" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Remove any copyright, trademark, or other proprietary notices from our services" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code of our software" />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            6. User Content
          </Typography>
          
          <Typography variant="body1" paragraph>
            Our services may allow you to post, link, store, share, and otherwise make available certain information, text, graphics, videos, or other material ("User Content").
            You are responsible for the User Content that you post through our services, including its legality, reliability, and appropriateness.
          </Typography>
          
          <Typography variant="body1" paragraph>
            By posting User Content, you grant us the right to use, modify, display, reproduce, and distribute such content on and through our services. 
            You retain any and all rights to any User Content you submit, post, or display on or through our services.
          </Typography>
          
          <Typography variant="body1" paragraph>
            You represent and warrant that:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText primary="You own or control all rights to the User Content you provide" />
            </ListItem>
            <ListItem>
              <ListItemText primary="The User Content does not violate any third-party rights, including intellectual property rights and privacy rights" />
            </ListItem>
            <ListItem>
              <ListItemText primary="The User Content complies with applicable laws and regulations" />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            7. Limitation of Liability
          </Typography>
          
          <Typography variant="body1" paragraph>
            In no event shall SOLVENDA, its directors, employees, partners, agents, suppliers, or affiliates be liable for:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText primary="Any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Any damages resulting from the inability to use our services or any content therein" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Any damages related to security breaches, viruses, or other harmful material that may infect your equipment" />
            </ListItem>
          </List>
          
          <Typography variant="body1" paragraph>
            The foregoing limitations will apply whether such damages arise out of breach of contract, tort (including negligence), or otherwise.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            8. Disclaimer
          </Typography>
          
          <Typography variant="body1" paragraph>
            Our services are provided on an "AS IS" and "AS AVAILABLE" basis, without any warranties of any kind, either express or implied.
            We do not warrant that our services will be uninterrupted, timely, secure, or error-free.
          </Typography>
          
          <Typography variant="body1" paragraph>
            We make no warranties regarding the accuracy, reliability, or completeness of the content on our services or the results that may be obtained from using our services.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            9. Termination
          </Typography>
          
          <Typography variant="body1" paragraph>
            We may terminate or suspend your account and access to our services immediately, without prior notice or liability, for any reason, including but not limited to a breach of these Terms.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Upon termination, your right to use our services will immediately cease. If you wish to terminate your account, you may simply discontinue using our services or contact us to request account deletion.
          </Typography>
          
          <Typography variant="body1" paragraph>
            All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            10. Governing Law
          </Typography>
          
          <Typography variant="body1" paragraph>
            These Terms shall be governed and construed in accordance with the laws of Albania, without regard to its conflict of law provisions.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. 
            If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom>
            11. Changes to Terms
          </Typography>
          
          <Typography variant="body1" paragraph>
            We reserve the right to modify or replace these Terms at any time. We will provide notice of any material changes by posting the new Terms on our website and updating the "Last Updated" date.
          </Typography>
          
          <Typography variant="body1" paragraph>
            Your continued use of our services after any such changes constitutes your acceptance of the new Terms. 
            It is your responsibility to review these Terms periodically for changes.
          </Typography>

        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage; 